import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { formatDateForInput, formatContractAreaSize } from './formatters';
import { fetchPdfTemplate, fetchPublicAsset, getPublicAssetPath } from './staticAssetLoader';

/**
 * 날짜를 YYYY.MM.DD 형식으로 변환
 */
const formatDateForPdf = (dateString) => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}.${month}.${day}`;
    } catch (error) {
        console.error('날짜 포맷팅 오류:', error);
        return '';
    }
};

/**
 * 금액에서 천 단위 구분자 제거하고 숫자만 반환
 */
const formatAmountForPdf = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '';

    const num = parseFloat(amount);
    if (isNaN(num)) return '';

    // 소수점 제거하고 정수로 변환
    return Math.floor(num).toString();
};

/**
 * 사망자 정보 추출 (첫 번째 사망자)
 */
const getDeceasedInfo = (contract) => {
    if (!contract?.deads || contract.deads.length === 0) {
        return {
            name: '',
            gender: '',
            residentNumber: '',
            deathDate: '',
            burialMethod: '',
            address: ''
        };
    }

    const firstDead = contract.deads[0];

    // 주소 조합
    const roadAddress = firstDead.road_address || '';
    const addressDetail = firstDead.address_detail || '';
    const address = firstDead.address || '';

    let fullAddress = '';
    if (roadAddress && addressDetail) {
        fullAddress = `${roadAddress} ${addressDetail}`;
    } else if (roadAddress) {
        fullAddress = roadAddress;
    } else if (address) {
        fullAddress = address;
    }

    // 장례구분 조합 (burial_method_1, burial_method_2)
    const burialMethod1 = firstDead.burial_method_1 || '';
    const burialMethod2 = firstDead.burial_method_2 || '';
    let burialMethod = '';
    if (burialMethod1 && burialMethod2) {
        burialMethod = `${burialMethod1}/${burialMethod2}`;
    } else if (burialMethod1) {
        burialMethod = burialMethod1;
    } else if (burialMethod2) {
        burialMethod = burialMethod2;
    }

    return {
        name: firstDead.name || '',
        gender: firstDead.gender || '',
        residentNumber: firstDead.resident_number || '',
        deathDate: firstDead.death_date || '',
        burialMethod: burialMethod,
        address: fullAddress
    };
};

/**
 * 연고자 정보 추출 (index번째 연고자, 기본 0)
 */
const getAcquaintanceInfo = (contract, index = 0) => {
    if (!contract?.acquaintances || contract.acquaintances.length <= index) {
        return {
            name: '',
            gender: '',
            residentNumber: '',
            phone: '',
            address: ''
        };
    }

    const acc = contract.acquaintances[index];

    // 주소 조합
    const roadAddress = acc.road_address || '';
    const addressDetail = acc.detail_address || acc.address_detail || '';
    const address = acc.address || '';

    let fullAddress = '';
    if (roadAddress && addressDetail) {
        fullAddress = `${roadAddress} ${addressDetail}`;
    } else if (roadAddress) {
        fullAddress = roadAddress;
    } else if (address) {
        fullAddress = address;
    }

    // 성별 추론 (주민등록번호에서)
    let gender = '';
    const residentNumber = acc.resident_number || '';
    if (residentNumber.length >= 8) {
        const genderDigit = residentNumber.charAt(7);
        if (genderDigit === '1' || genderDigit === '3') {
            gender = '남';
        } else if (genderDigit === '2' || genderDigit === '4') {
            gender = '여';
        }
    }

    return {
        name: acc.name || '',
        gender: gender,
        residentNumber: residentNumber,
        phone: acc.phone || '',
        address: fullAddress
    };
};

/**
 * PDF 폼 필드명 확인 (디버깅용)
 * @returns {Promise<Array>} - 필드명 배열
 */
export const getPdfFormFields = async () => {
    try {
        const pdfBytes = await fetchPdfTemplate('myojeokbu_form.pdf');
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const form = pdfDoc.getForm();

        const fields = form.getFields();
        const fieldNames = fields.map(field => ({
            name: field.getName(),
            type: field.constructor.name
        }));

        console.log('PDF 폼 필드 목록:', fieldNames);
        return fieldNames;
    } catch (error) {
        console.error('PDF 필드 확인 오류:', error);
        throw error;
    }
};

/**
 * PDF 폼 필드 채우기
 * @param {Object} contract - 계약 정보
 * @param {Array} managementFees - 관리비 내역 배열
 * @returns {Promise<Uint8Array>} - 채워진 PDF 바이트 배열
 */
/**
 * 한글 텍스트를 안전하게 처리 (한글이 포함된 경우 빈 문자열로 대체하거나 ASCII만 사용)
 * pdf-lib는 기본적으로 한글을 지원하지 않으므로, 텍스트 필드에 직접 한글을 입력할 수 없습니다.
 * 대신 텍스트 필드의 updateAppearances를 사용하거나, 한글을 제거/변환합니다.
 */
const sanitizeTextForPdf = (text) => {
    if (!text) return '';

    // 한글이 포함되어 있으면 경고하고 빈 문자열 반환
    // 또는 ASCII 문자만 유지
    const hasKorean = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(text);
    if (hasKorean) {
        // 한글을 포함한 텍스트는 그대로 유지하되, 
        // pdf-lib가 처리할 수 있도록 updateAppearances 사용
        return text;
    }
    return text;
};

const loadKoreanFont = async (pdfDoc) => {
    const fontPaths = [
        '/fonts/NanumGothic.ttf',
        '/fonts/NotoSansKR-Regular.ttf',
        '/fonts/malgun.ttf',
    ];

    for (const fontPath of fontPaths) {
        try {
            const fontBytes = await fetchPublicAsset(fontPath, { optional: true });
            if (!fontBytes) {
                continue;
            }
            const koreanFont = await pdfDoc.embedFont(fontBytes);
            console.log(`✅ 한글 폰트 로드 성공: ${getPublicAssetPath(fontPath)}`);
            return koreanFont;
        } catch (fontError) {
            console.warn(`폰트 로드 오류 (${fontPath}):`, fontError.message);
        }
    }

    console.error('❌ 한글 폰트를 로드할 수 없습니다. 기본 폰트를 사용합니다.');
    return null;
};

export const fillPdfForm = async (contract, managementFees = []) => {
    let pdfDoc = null;

    try {
        const pdfBytes = await fetchPdfTemplate('myojeokbu_form.pdf');
        pdfDoc = await PDFDocument.load(pdfBytes);

        // fontkit 등록 (한글 폰트 지원)
        pdfDoc.registerFontkit(fontkit);

        const koreanFont = await loadKoreanFont(pdfDoc);

        // 폼 가져오기
        const form = pdfDoc.getForm();

        // 사망자 정보 추출 (첫 번째)
        const deceased = getDeceasedInfo(contract);

        // 연고자 정보 추출 (1·2번)
        const acquaintance = getAcquaintanceInfo(contract, 0);
        const acquaintance2 = getAcquaintanceInfo(contract, 1);

        // 묘역위치 조합 (단지-묘역-열-번호)
        const mausoleumLocation = [
            contract?.mausoleum?.complex,
            contract?.mausoleum?.area,
            contract?.mausoleum?.row_number,
            contract?.mausoleum?.number
        ].filter(Boolean).join('-') || '';

        const contractAreaPyeong = formatContractAreaSize(contract?.mausoleum?.area_size);

        // 기본 필드 채우기
        const basicFields = {
            '묘적번호': contract?.mausoleum?.member_number || '',
            '등록일자': formatDateForPdf(contract?.contract_date || contract?.created_at),
            '계약평수': contractAreaPyeong ? `${contractAreaPyeong}평` : '',
            '묘역위치': mausoleumLocation,
            '사망일시': formatDateForPdf(deceased.deathDate),
            '사망자_성명': deceased.name,
            '사망자_성별': deceased.gender,
            '사망자_주민번호': deceased.residentNumber,
            '장례구분': deceased.burialMethod,
            '사망자_주소': deceased.address,
            '연고자_성명': acquaintance.name,
            '연고자_성별': acquaintance.gender,
            '연고자_주민번호': acquaintance.residentNumber,
            '연고자_전화번호': acquaintance.phone,
            '연고자_주소': acquaintance.address,
            '연고자2_성명': acquaintance2.name,
            '연고자2_성별': acquaintance2.gender,
            '연고자2_주민번호': acquaintance2.residentNumber,
            '연고자2_전화번호': acquaintance2.phone,
            '연고자2_주소': acquaintance2.address
        };

        // 모든 필드명 가져오기 (디버깅용)
        const allFields = form.getFields();
        const fieldNamesMap = {};
        allFields.forEach(field => {
            const name = field.getName();
            fieldNamesMap[name] = field;
        });

        // 디버깅: 사용 가능한 필드명 출력
        console.log('PDF 폼 필드명 목록:', Object.keys(fieldNamesMap));

        // 기본 필드 채우기 (필드명이 다를 수 있으므로 시도)
        // 한글 인코딩 오류를 처리하기 위해 각 필드를 개별적으로 처리
        for (const [fieldName, value] of Object.entries(basicFields)) {
            try {
                // 정확한 필드명으로 시도
                if (fieldNamesMap[fieldName]) {
                    const field = form.getTextField(fieldName);
                    if (field) {
                        // 텍스트 필드에 값을 설정
                        // pdf-lib는 setText 호출 시 내부적으로 updateAppearances를 호출하는데,
                        // 이 과정에서 한글 인코딩 오류가 발생할 수 있습니다.
                        // 하지만 텍스트 값 자체는 PDF에 저장되므로, 뷰어가 읽을 수 있습니다.

                        // 텍스트 필드에 값을 설정
                        // pdf-lib는 setText 호출 시 내부적으로 updateAppearances를 호출하는데,
                        // 이 과정에서 한글 인코딩 오류가 발생할 수 있습니다.
                        // 하지만 텍스트 값 자체는 PDF 객체에 저장되므로, 뷰어가 읽을 수 있습니다.

                        // 텍스트 필드에 값을 설정
                        // 한글 폰트가 있는 경우에도 setText 호출 시 오류가 발생할 수 있으므로
                        // 오류를 catch하여 처리
                        try {
                            field.setText(value);
                            console.log(`필드 "${fieldName}" 채움: ${value}`);
                        } catch (setTextError) {
                            // WinAnsi 인코딩 오류는 무시하고 계속 진행
                            // 나중에 폰트를 적용할 때 다시 시도할 수 있음
                            const errorMessage = setTextError.message || String(setTextError);
                            if (errorMessage.includes('WinAnsi') ||
                                errorMessage.includes('encode') ||
                                errorMessage.includes('Encoding')) {
                                console.log(`필드 "${fieldName}" 한글 텍스트 설정 (인코딩 경고, 나중에 폰트 적용): ${value}`);
                                // 텍스트 값은 PDF 객체에 저장되었을 수 있음
                                // 나중에 폰트를 적용할 때 다시 시도
                            } else {
                                // 다른 종류의 오류는 다시 throw
                                throw setTextError;
                            }
                        }
                    }
                } else {
                    // 필드명을 찾지 못한 경우 경고
                    console.warn(`필드 "${fieldName}"을 찾을 수 없습니다.`);
                }
            } catch (error) {
                // 전체 오류를 무시하지 않고, 특정 인코딩 오류만 처리
                const errorMessage = error.message || String(error);
                if (errorMessage.includes('WinAnsi') ||
                    errorMessage.includes('encode') ||
                    errorMessage.includes('Encoding')) {
                    console.log(`필드 "${fieldName}" 한글 텍스트 처리 완료 (인코딩 경고 무시)`);
                } else {
                    console.warn(`필드 "${fieldName}" 채우기 실패:`, errorMessage);
                }
            }
        }

        // 관리비 내역 채우기 (최대 13개)
        const feesToFill = managementFees.slice(0, 13);

        // 합계 계산
        let totalPaid = 0;      // 입금합계
        let totalUnpaid = 0;    // 미입금합계

        managementFees.forEach(fee => {
            const amount = parseFloat(fee.amount) || 0;
            if (fee.is_paid || fee.paid_at) {
                totalPaid += amount;
            } else {
                totalUnpaid += amount;
            }
        });

        feesToFill.forEach((fee, index) => {
            const n = index + 1;

            const feeFields = {
                [`입금구분${n}`]: '관리비',
                [`회차${n}`]: `${n}`,
                [`관리시작일${n}`]: formatDateForPdf(fee.fee_period_start || fee.period_start),
                [`관리종료일${n}`]: formatDateForPdf(fee.fee_period_end || fee.period_end),
                [`금액${n}`]: formatAmountForPdf(fee.amount),
                [`입금방법${n}`]: fee.is_paid ? '납부완료' : (fee.paid_at ? '납부완료' : '미납'),
                [`메모${n}`]: fee.memo || fee.description || ''
            };

            // 관리비 필드 채우기 (한글 인코딩 오류 처리)
            for (const [fieldName, value] of Object.entries(feeFields)) {
                try {
                    // 정확한 필드명으로 시도
                    if (fieldNamesMap[fieldName]) {
                        const field = form.getTextField(fieldName);
                        if (field) {
                            try {
                                field.setText(value);
                                console.log(`필드 "${fieldName}" 채움: ${value}`);
                            } catch (setTextError) {
                                // WinAnsi 인코딩 오류는 무시하고 계속 진행
                                // 나중에 폰트를 적용할 때 다시 시도할 수 있음
                                const errorMessage = setTextError.message || String(setTextError);
                                if (errorMessage.includes('WinAnsi') ||
                                    errorMessage.includes('encode') ||
                                    errorMessage.includes('Encoding')) {
                                    console.log(`필드 "${fieldName}" 한글 텍스트 설정 (인코딩 경고, 나중에 폰트 적용): ${value}`);
                                    // 텍스트 값은 PDF 객체에 저장되었을 수 있음
                                } else {
                                    throw setTextError;
                                }
                            }
                        }
                    } else {
                        console.warn(`필드 "${fieldName}"을 찾을 수 없습니다.`);
                    }
                } catch (error) {
                    // 전체 오류를 무시하지 않고, 특정 인코딩 오류만 처리
                    const errorMessage = error.message || String(error);
                    if (errorMessage.includes('WinAnsi') ||
                        errorMessage.includes('encode') ||
                        errorMessage.includes('Encoding')) {
                        console.log(`필드 "${fieldName}" 한글 텍스트 처리 완료 (인코딩 경고 무시)`);
                    } else {
                        console.warn(`필드 "${fieldName}" 채우기 실패:`, errorMessage);
                    }
                }
            }
        });

        // 합계 필드 채우기
        const totalFields = {
            '미입금합계': formatAmountForPdf(totalUnpaid),
            '입금합계': formatAmountForPdf(totalPaid)
        };

        for (const [fieldName, value] of Object.entries(totalFields)) {
            try {
                if (fieldNamesMap[fieldName]) {
                    const field = form.getTextField(fieldName);
                    if (field) {
                        try {
                            field.setText(value);
                            console.log(`필드 "${fieldName}" 채움: ${value}`);
                        } catch (setTextError) {
                            const errorMessage = setTextError.message || String(setTextError);
                            if (errorMessage.includes('WinAnsi') ||
                                errorMessage.includes('encode') ||
                                errorMessage.includes('Encoding')) {
                                console.log(`필드 "${fieldName}" 한글 텍스트 설정 (인코딩 경고, 나중에 폰트 적용): ${value}`);
                            } else {
                                throw setTextError;
                            }
                        }
                    }
                } else {
                    console.warn(`필드 "${fieldName}"을 찾을 수 없습니다.`);
                }
            } catch (error) {
                const errorMessage = error.message || String(error);
                if (errorMessage.includes('WinAnsi') ||
                    errorMessage.includes('encode') ||
                    errorMessage.includes('Encoding')) {
                    console.log(`필드 "${fieldName}" 한글 텍스트 처리 완료 (인코딩 경고 무시)`);
                } else {
                    console.warn(`필드 "${fieldName}" 채우기 실패:`, errorMessage);
                }
            }
        }

        // PDF 저장 전에 모든 텍스트 필드의 appearance를 한글 폰트로 업데이트
        // 한글 폰트가 로드된 경우, 모든 필드의 appearance를 폰트와 함께 업데이트
        if (koreanFont) {
            console.log('한글 폰트를 사용하여 모든 텍스트 필드의 appearance를 업데이트합니다.');

            try {
                // 폼의 모든 필드에 대해 appearance를 한글 폰트로 업데이트
                // 이렇게 하면 모든 텍스트 필드가 한글 폰트를 사용하게 됩니다
                form.updateFieldAppearances(koreanFont);
                console.log('모든 텍스트 필드의 appearance를 한글 폰트로 업데이트했습니다.');
            } catch (updateError) {
                console.warn('폼 필드 appearance 업데이트 실패, 개별 필드에 대해 시도:', updateError.message);

                // 개별 필드에 대해 업데이트 시도
                const allFields = form.getFields();
                let successCount = 0;
                let failCount = 0;

                allFields.forEach(field => {
                    if (field.constructor.name === 'PDFTextField') {
                        try {
                            field.updateAppearances(koreanFont);
                            successCount++;
                        } catch (fieldError) {
                            failCount++;
                            console.warn(`필드 "${field.getName()}" appearance 업데이트 실패:`, fieldError.message);
                        }
                    }
                });

                console.log(`개별 필드 appearance 업데이트 완료: 성공 ${successCount}개, 실패 ${failCount}개`);
            }
        } else {
            console.warn('한글 폰트가 로드되지 않았습니다. 기본 폰트를 사용합니다.');
        }

        // PDF 저장
        // 한글 폰트가 적용된 경우, appearance 업데이트 오류가 발생하지 않아야 합니다.
        try {
            const filledPdfBytes = await pdfDoc.save();
            return filledPdfBytes;
        } catch (saveError) {
            const errorMessage = saveError.message || String(saveError);

            // WinAnsi 인코딩 오류인 경우 처리
            if (errorMessage.includes('WinAnsi') ||
                errorMessage.includes('encode') ||
                errorMessage.includes('Encoding')) {

                console.warn('PDF 저장 중 인코딩 경고 발생 (appearance 업데이트 오류):', errorMessage);
                console.log('텍스트 필드의 값은 이미 PDF 객체에 저장되어 있습니다.');
                console.log('PDF 뷰어가 텍스트 필드의 값을 읽어서 자동으로 렌더링할 수 있습니다.');

                // pdf-lib의 내부 동작을 우회하기 위해,
                // 텍스트 필드의 값을 직접 PDF 객체에 설정한 후 저장하려고 시도
                // 하지만 이는 복잡하므로, 대신 사용자에게 알림

                // 실제로는 텍스트 필드의 값이 PDF 객체에 저장되어 있으므로,
                // appearance 업데이트 오류를 무시하고 PDF를 저장할 수 있습니다.
                // 하지만 pdf-lib는 이를 허용하지 않으므로, 다른 방법이 필요합니다.

                // 최종 해결책: 한글 폰트를 임베딩하여 사용하거나,
                // 또는 텍스트 필드의 값을 설정하되, appearance를 업데이트하지 않도록 해야 합니다.
                // 하지만 pdf-lib는 이를 직접 지원하지 않으므로, 
                // 사용자에게 PDF 뷰어에서 확인하도록 안내합니다.

                // 임시 해결책: 오류를 무시하고 PDF를 저장하려고 시도
                // 하지만 이는 작동하지 않을 수 있으므로, 사용자에게 알림

                throw new Error(
                    `PDF 저장 중 한글 인코딩 오류가 발생했습니다.\n\n` +
                    `원인: pdf-lib는 텍스트 필드의 appearance를 업데이트할 때 WinAnsi 인코딩을 사용하므로, ` +
                    `한글 문자를 인코딩할 수 없습니다.\n\n` +
                    `해결 방법:\n` +
                    `1. 한글 폰트 파일(예: NanumGothic.ttf)을 public/fonts/ 폴더에 추가하세요.\n` +
                    `2. 또는 PDF 뷰어에서 텍스트 필드의 값을 확인할 수 있습니다.\n\n` +
                    `참고: 텍스트 필드의 값은 PDF 객체에 저장되어 있으므로, ` +
                    `PDF 뷰어가 텍스트 필드의 값을 읽어서 자동으로 렌더링할 수 있습니다.`
                );
            } else {
                throw saveError;
            }
        }

    } catch (error) {
        // 최상위 오류 처리
        const errorMessage = error.message || String(error);

        // 인코딩 관련 오류는 무시하고 PDF 저장 시도
        if (errorMessage.includes('WinAnsi') ||
            errorMessage.includes('encode') ||
            errorMessage.includes('Encoding')) {
            console.warn('PDF 생성 중 인코딩 경고 발생, PDF 저장 시도:', errorMessage);

            try {
                // PDF를 저장하여 텍스트 값이 포함된 PDF 생성 시도
                const filledPdfBytes = await pdfDoc.save();
                console.log('PDF 저장 성공 (인코딩 경고 무시)');
                return filledPdfBytes;
            } catch (saveError) {
                console.error('PDF 저장 실패:', saveError);
                throw new Error(`PDF 생성 실패: ${saveError.message}`);
            }
        } else {
            console.error('PDF 폼 채우기 오류:', error);
            throw error;
        }
    }
};

/**
 * PDF 폼 채우기 (묘적부 - 여러 사망자/연고자 지원)
 * myojeokbu_form2.pdf를 사용하여 사망자 최대 2명, 연고자 최대 4명 정보 입력
 * @param {Object} contract - 계약 정보
 * @returns {Promise<Uint8Array>} - 채워진 PDF 바이트 배열
 */
export const fillPdfForm2 = async (contract) => {
    let pdfDoc = null;

    try {
        const pdfBytes = await fetchPdfTemplate('myojeokbu_form2.pdf');
        pdfDoc = await PDFDocument.load(pdfBytes);

        // fontkit 등록 (한글 폰트 지원)
        pdfDoc.registerFontkit(fontkit);

        const koreanFont = await loadKoreanFont(pdfDoc);

        // 폼 가져오기
        const form = pdfDoc.getForm();

        // 묘역위치 조합
        const mausoleumLocation = [
            contract?.mausoleum?.complex,
            contract?.mausoleum?.area,
            contract?.mausoleum?.row_number,
            contract?.mausoleum?.number
        ].filter(Boolean).join('-') || '';

        // 계약면적 계산 (평수 * 3.3 = 제곱미터)
        const areaSize = parseFloat(contract?.mausoleum?.area_size) || 0;
        const areaSizeInSquareMeters = areaSize > 0 ? (areaSize * 3.3).toFixed(2) : '';
        const contractAreaPyeongForm2 = formatContractAreaSize(contract?.mausoleum?.area_size);

        // 설묘일 (사망자 중 가장 빠른 설묘일 또는 첫 번째 사망자의 설묘일)
        const reportDate = contract?.deads?.[0]?.report_date || contract?.deads?.[0]?.burial_date || '';

        const baseMemberNumber = contract?.mausoleum?.member_number || contract?.id?.toString() || '';
        const tombCount = contract?.mausoleum?.number_of_tombs ?? '';

        // 기본 필드 채우기
        const basicFields = {
            '묘적번호': baseMemberNumber,
            '설묘일': formatDateForPdf(reportDate),
            '단지': contract?.mausoleum?.complex || '',
            '묘역': contract?.mausoleum?.area || '',
            '열': contract?.mausoleum?.row_number || '',
            '호': contract?.mausoleum?.number || '',
            '기수1': tombCount !== null && tombCount !== undefined ? String(tombCount) : '',
            '계약면적': areaSizeInSquareMeters ? `${areaSizeInSquareMeters}㎡` : '',
            '계약평수': contractAreaPyeongForm2 ? `${contractAreaPyeongForm2}평` : '',
            '등록일자': formatDateForPdf(contract?.contract_date || contract?.created_at),
            '묘역위치': mausoleumLocation
        };

        // 사망자가 2명인 경우, 보조 위치 필드(단지1/묘역1/열1/호1)에도 동일 값 채움
        if ((contract?.deads || []).length >= 2) {
            basicFields['단지1'] = contract?.mausoleum?.complex || '';
            basicFields['묘역1'] = contract?.mausoleum?.area || '';
            basicFields['열1'] = contract?.mausoleum?.row_number || '';
            basicFields['호1'] = contract?.mausoleum?.number || '';
        }

        // 모든 필드명 가져오기
        const allFields = form.getFields();
        const fieldNamesMap = {};
        allFields.forEach(field => {
            const name = field.getName();
            fieldNamesMap[name] = field;
        });

        console.log('PDF 폼 필드명 목록:', Object.keys(fieldNamesMap));

        // 기본 필드 채우기
        for (const [fieldName, value] of Object.entries(basicFields)) {
            try {
                if (fieldNamesMap[fieldName]) {
                    const field = form.getTextField(fieldName);
                    if (field) {
                        try {
                            field.setText(value);
                            console.log(`필드 "${fieldName}" 채움: ${value}`);
                        } catch (setTextError) {
                            const errorMessage = setTextError.message || String(setTextError);
                            if (errorMessage.includes('WinAnsi') ||
                                errorMessage.includes('encode') ||
                                errorMessage.includes('Encoding')) {
                                console.log(`필드 "${fieldName}" 한글 텍스트 설정 (인코딩 경고)`);
                            } else {
                                throw setTextError;
                            }
                        }
                    }
                } else {
                    console.warn(`필드 "${fieldName}"을 찾을 수 없습니다.`);
                }
            } catch (error) {
                const errorMessage = error.message || String(error);
                if (errorMessage.includes('WinAnsi') ||
                    errorMessage.includes('encode') ||
                    errorMessage.includes('Encoding')) {
                    console.log(`필드 "${fieldName}" 한글 텍스트 처리 완료`);
                } else {
                    console.warn(`필드 "${fieldName}" 채우기 실패:`, errorMessage);
                }
            }
        }

        // 사망자 정보 채우기 (최대 2명, PDF 필드가 2명까지만 지원)
        const deadsToFill = (contract?.deads || []).slice(0, 2);

        const deadOffset = Number(contract?.__dead_offset || 0);

        deadsToFill.forEach((dead, index) => {
            const n = index + 1;
            const globalDeadIndex = deadOffset + index;
            const numberedMemberNumber = globalDeadIndex === 0
                ? baseMemberNumber
                : `${baseMemberNumber}-${globalDeadIndex}`;

            // 주소 조합
            const roadAddress = dead.road_address || '';
            const addressDetail = dead.address_detail || '';
            const address = dead.address || '';

            let fullAddress = '';
            if (roadAddress && addressDetail) {
                fullAddress = `${roadAddress} ${addressDetail}`;
            } else if (roadAddress) {
                fullAddress = roadAddress;
            } else if (address) {
                fullAddress = address;
            }

            // 장례구분 조합
            const burialMethod1 = dead.burial_method_1 || '';
            const burialMethod2 = dead.burial_method_2 || '';
            let burialMethod = '';
            if (burialMethod1 && burialMethod2) {
                burialMethod = `${burialMethod1}/${burialMethod2}`;
            } else if (burialMethod1) {
                burialMethod = burialMethod1;
            } else if (burialMethod2) {
                burialMethod = burialMethod2;
            }

            // 연고자와의 관계 (첫 번째 연고자 기준, 없으면 빈 값)
            const relationship = contract?.acquaintances?.[0]?.relationship || '';

            const deadFields = {
                [`묘적번호${n}`]: numberedMemberNumber,
                [`사망자${n} 성명`]: dead.name || '',
                [`사망자${n} 사망일시`]: formatDateForPdf(dead.death_date),
                [`사망자${n} 설묘일`]: formatDateForPdf(dead.report_date || dead.burial_date),
                [`사망자${n} 성별`]: dead.gender || '',
                [`사망자${n} 주민번호`]: dead.resident_number || '',
                [`사망자${n} 사망원인`]: dead.death_cause || '',
                [`사망자${n} 관계`]: relationship,
                [`사망자${n} 주소`]: fullAddress,
                [`사망자${n} 사망장소`]: dead.death_place || '',
                [`사망자${n} 장례구분`]: burialMethod
            };

            // 사망자 필드 채우기
            for (const [fieldName, value] of Object.entries(deadFields)) {
                try {
                    if (fieldNamesMap[fieldName]) {
                        const field = form.getTextField(fieldName);
                        if (field) {
                            try {
                                field.setText(value);
                                console.log(`필드 "${fieldName}" 채움: ${value}`);
                            } catch (setTextError) {
                                const errorMessage = setTextError.message || String(setTextError);
                                if (errorMessage.includes('WinAnsi') ||
                                    errorMessage.includes('encode') ||
                                    errorMessage.includes('Encoding')) {
                                    console.log(`필드 "${fieldName}" 한글 텍스트 설정`);
                                } else {
                                    throw setTextError;
                                }
                            }
                        }
                    } else {
                        console.warn(`필드 "${fieldName}"을 찾을 수 없습니다.`);
                    }
                } catch (error) {
                    const errorMessage = error.message || String(error);
                    if (!(errorMessage.includes('WinAnsi') ||
                        errorMessage.includes('encode') ||
                        errorMessage.includes('Encoding'))) {
                        console.warn(`필드 "${fieldName}" 채우기 실패:`, errorMessage);
                    }
                }
            }
        });

        // 연고자 정보 채우기 (최대 4명)
        const acquaintancesToFill = (contract?.acquaintances || []).slice(0, 4);

        acquaintancesToFill.forEach((acquaintance, index) => {
            const n = index + 1;

            // 주소 조합
            const roadAddress = acquaintance.road_address || '';
            const address = acquaintance.address || '';
            const baseAddress = roadAddress || address || '';
            const addressDetail = acquaintance.detail_address || acquaintance.address_detail || '';

            let fullAddress = '';
            if (baseAddress && addressDetail) {
                fullAddress = `${baseAddress}, ${addressDetail}`;
            } else if (baseAddress) {
                fullAddress = baseAddress;
            } else if (addressDetail) {
                fullAddress = addressDetail;
            }

            // 성별 추론 (주민등록번호에서)
            let gender = '';
            const residentNumber = acquaintance.resident_number || '';
            if (residentNumber.length >= 8) {
                const genderDigit = residentNumber.charAt(7);
                if (genderDigit === '1' || genderDigit === '3') {
                    gender = '남';
                } else if (genderDigit === '2' || genderDigit === '4') {
                    gender = '여';
                }
            }

            const acquaintanceFields = {
                [`연고자${n} 성명`]: acquaintance.name || '',
                [`연고자${n} 주민번호`]: residentNumber,
                [`연고자${n} 연락처`]: acquaintance.phone || '',
                [`연고자${n} 성별`]: gender,
                [`연고자${n} 주소`]: fullAddress,
                [`연고자${n} 관계`]: acquaintance.relationship || ''
            };

            // 연고자 필드 채우기
            for (const [fieldName, value] of Object.entries(acquaintanceFields)) {
                try {
                    if (fieldNamesMap[fieldName]) {
                        const field = form.getTextField(fieldName);
                        if (field) {
                            try {
                                field.setText(value);
                                console.log(`필드 "${fieldName}" 채움: ${value}`);
                            } catch (setTextError) {
                                const errorMessage = setTextError.message || String(setTextError);
                                if (errorMessage.includes('WinAnsi') ||
                                    errorMessage.includes('encode') ||
                                    errorMessage.includes('Encoding')) {
                                    console.log(`필드 "${fieldName}" 한글 텍스트 설정`);
                                } else {
                                    throw setTextError;
                                }
                            }
                        }
                    } else {
                        console.warn(`필드 "${fieldName}"을 찾을 수 없습니다.`);
                    }
                } catch (error) {
                    const errorMessage = error.message || String(error);
                    if (!(errorMessage.includes('WinAnsi') ||
                        errorMessage.includes('encode') ||
                        errorMessage.includes('Encoding'))) {
                        console.warn(`필드 "${fieldName}" 채우기 실패:`, errorMessage);
                    }
                }
            }
        });

        // 한글 폰트로 appearance 업데이트
        if (koreanFont) {
            try {
                form.updateFieldAppearances(koreanFont);
                console.log('모든 텍스트 필드의 appearance를 한글 폰트로 업데이트했습니다.');
            } catch (updateError) {
                console.warn('폼 필드 appearance 업데이트 실패:', updateError.message);
            }
        }

        // PDF 저장
        const filledPdfBytes = await pdfDoc.save();
        return filledPdfBytes;

    } catch (error) {
        const errorMessage = error.message || String(error);

        if (errorMessage.includes('WinAnsi') ||
            errorMessage.includes('encode') ||
            errorMessage.includes('Encoding')) {
            console.warn('PDF 생성 중 인코딩 경고 발생, PDF 저장 시도:', errorMessage);

            try {
                const filledPdfBytes = await pdfDoc.save();
                console.log('PDF 저장 성공 (인코딩 경고 무시)');
                return filledPdfBytes;
            } catch (saveError) {
                console.error('PDF 저장 실패:', saveError);
                throw new Error(`PDF 생성 실패: ${saveError.message}`);
            }
        } else {
            console.error('PDF 폼 채우기 오류:', error);
            throw error;
        }
    }
};

/**
 * myojeokbu_form2.pdf용 계약 데이터를 청크로 분할
 * - 파일 1개당 사망자 최대 2명
 * - 파일 1개당 연고자 최대 4명
 */
export const splitContractForPdfForm2 = (contract) => {
    const deads = Array.isArray(contract?.deads) ? contract.deads : [];
    const acquaintances = Array.isArray(contract?.acquaintances) ? contract.acquaintances : [];

    const deadChunkSize = 2;
    const acquaintanceChunkSize = 4;

    const deadChunkCount = Math.ceil(deads.length / deadChunkSize);
    const acquaintanceChunkCount = Math.ceil(acquaintances.length / acquaintanceChunkSize);
    const totalChunks = Math.max(1, deadChunkCount, acquaintanceChunkCount);

    return Array.from({ length: totalChunks }, (_, index) => {
        const deadStart = index * deadChunkSize;
        const acquaintanceStart = index * acquaintanceChunkSize;

        return {
            ...contract,
            __dead_offset: deadStart,
            deads: deads.slice(deadStart, deadStart + deadChunkSize),
            acquaintances: acquaintances.slice(acquaintanceStart, acquaintanceStart + acquaintanceChunkSize)
        };
    });
};

/**
 * myojeokbu_form2.pdf 여러 건 생성
 * 초과 인원이 있으면 청크 단위로 여러 PDF 바이트를 반환
 */
export const fillPdfForm2Multiple = async (contract) => {
    const chunkedContracts = splitContractForPdfForm2(contract);
    const results = [];

    for (let i = 0; i < chunkedContracts.length; i++) {
        const pdfBytes = await fillPdfForm2(chunkedContracts[i]);
        results.push({
            index: i + 1,
            total: chunkedContracts.length,
            pdfBytes
        });
    }

    return results;
};

/**
 * PDF Blob URL 생성
 */
const createPdfBlobUrl = (pdfBytes) => {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
};

export {
    createPdfPreviewTab,
    showPdfInPreviewTab,
    showPdfPreviewError,
} from './pdfPreview';

/**
 * PDF 다운로드
 * @param {Uint8Array} pdfBytes - PDF 바이트 배열
 * @param {string} fileName - 파일명
 */
export const downloadPdf = (pdfBytes, fileName) => {
    const url = createPdfBlobUrl(pdfBytes);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

